import React, { useState, useCallback } from 'react';
import { prepare, layout } from '@chenglou/pretext';
import { SAMPLE_TEXTS, FONT_SIZE, LINE_HEIGHT, MAX_WIDTH } from '../config';

interface MeasureResult {
  text: string;
  height: number;
  lineCount: number;
  prepareDurationMs: number;
  layoutDurationMs: number;
}

export default function PretextPage() {
  const [results, setResults] = useState<MeasureResult[]>([]);
  const [totalMs, setTotalMs] = useState<number | null>(null);
  const [prepareMs, setPrepareMs] = useState<number | null>(null);
  const [layoutMs, setLayoutMs] = useState<number | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const measure = useCallback(() => {
    setIsRunning(true);
    setResults([]);
    setTotalMs(null);

    const font = `${FONT_SIZE}px sans-serif`;
    const allResults: MeasureResult[] = [];
    const overallStart = performance.now();
    let totalPrepareMs = 0;
    let totalLayoutMs = 0;

    for (const text of SAMPLE_TEXTS) {
      // Step 1: prepare() — one-time work: segment, measure glyphs via canvas
      const prepareStart = performance.now();
      const prepared = prepare(text, font);
      const prepareEnd = performance.now();
      const prepDuration = prepareEnd - prepareStart;
      totalPrepareMs += prepDuration;

      // Step 2: layout() — pure arithmetic, NO DOM access, NO reflow
      const layoutStart = performance.now();
      const result = layout(prepared, MAX_WIDTH, LINE_HEIGHT);
      const layoutEnd = performance.now();
      const layDuration = layoutEnd - layoutStart;
      totalLayoutMs += layDuration;

      allResults.push({
        text,
        height: result.height,
        lineCount: result.lineCount,
        prepareDurationMs: prepDuration,
        layoutDurationMs: layDuration,
      });
    }

    const overallEnd = performance.now();
    setResults(allResults);
    setTotalMs(overallEnd - overallStart);
    setPrepareMs(totalPrepareMs);
    setLayoutMs(totalLayoutMs);
    setIsRunning(false);

    console.log(`pretext approach: 0 DOM reflows. Layout total: ${totalLayoutMs.toFixed(3)}ms`);
  }, []);

  return (
    <div className="page">
      <div className="page-header pretext-header">
        <h2>⚡ pretext Library Measurement</h2>
        <p>
          Uses <code>@chenglou/pretext</code> — measures text height via Canvas font metrics
          and pure arithmetic. <strong>Zero DOM layout reflows.</strong>
        </p>
        <div className="how-it-works">
          <h4>How it works:</h4>
          <ol>
            <li>
              <code>prepare(text, font)</code> — segments text, measures glyphs using{' '}
              <code>CanvasRenderingContext2D.measureText()</code> (no layout reflow), caches widths
            </li>
            <li>
              <code>layout(prepared, maxWidth, lineHeight)</code> — pure arithmetic over cached
              widths → returns <code>{'{ height, lineCount }'}</code> instantly
            </li>
          </ol>
        </div>
        <div className="success-box">
          ✅ <strong>The advantage:</strong> <code>layout()</code> never touches the DOM.
          You can call it thousands of times (e.g. in a virtual list's height estimator) without
          causing a single reflow. Supports multilingual text, emoji, and mixed bidirectional text.
        </div>
      </div>

      <button
        className="run-btn pretext-btn"
        onClick={measure}
        disabled={isRunning}
      >
        {isRunning ? '⏳ Measuring...' : '▶ Run pretext Measurement'}
      </button>

      {totalMs !== null && (
        <div className="summary pretext-summary">
          <span>✅ Measured {results.length} texts</span>
          <span>⏱ Total time: <strong>{totalMs!.toFixed(2)}ms</strong></span>
          <span>🎨 prepare() total: <strong>{prepareMs!.toFixed(2)}ms</strong></span>
          <span>⚡ layout() total: <strong>{layoutMs!.toFixed(3)}ms</strong></span>
          <span>🔄 Layout reflows: <strong>0</strong></span>
        </div>
      )}

      {results.length > 0 && (
        <div className="results">
          {results.map((r, i) => (
            <div key={i} className="result-card">
              <div className="result-meta">
                <span className="badge pretext-badge">No DOM reflow</span>
                <span className="duration">
                  prepare: {r.prepareDurationMs.toFixed(3)}ms / layout: {r.layoutDurationMs.toFixed(3)}ms
                </span>
              </div>
              <p className="result-text">{r.text.slice(0, 80)}{r.text.length > 80 ? '…' : ''}</p>
              <div className="result-stats">
                <span>📏 Height: <strong>{r.height.toFixed(1)}px</strong></span>
                <span>↕ Lines: <strong>{r.lineCount}</strong></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
